import React, { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dbService } from '../lib/supabase';
import { decryptId, isValidEncryptedId } from '../utils/encryption';

interface ConversationGuardProps {
  children: React.ReactNode;
  userRole: 'startup' | 'cdmo';
}

const ConversationGuard: React.FC<ConversationGuardProps> = ({ children, userRole }) => {
  const { user } = useAuth();
  const { conversationId: encryptedConversationId } = useParams<{ conversationId: string }>();
  const [authStatus, setAuthStatus] = useState<'loading' | 'authorized' | 'unauthorized' | 'error'>('loading');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const validateConversationAccess = async () => {
      try {
        setAuthStatus('loading');

        // If no conversation ID in URL, allow access (dashboard main page)
        if (!encryptedConversationId) {
          setAuthStatus('authorized');
          setConversationId(null);
          return;
        }

        console.log('🔍 Validating conversation access for encrypted ID:', encryptedConversationId);

        // Validate encrypted ID format
        if (!isValidEncryptedId(encryptedConversationId)) {
          console.warn('❌ Invalid encrypted conversation ID format');
          setErrorMessage('The conversation link appears to be invalid or corrupted.');
          setAuthStatus('unauthorized');
          return;
        }

        // Decrypt conversation ID
        let decryptedId: string;
        try {
          decryptedId = decryptId(encryptedConversationId);
          console.log('🔓 Decrypted conversation ID:', decryptedId);
          setConversationId(decryptedId);
        } catch (decryptError) {
          console.error('❌ Failed to decrypt conversation ID:', decryptError);
          setErrorMessage('Unable to access this conversation. The link may be expired or invalid.');
          setAuthStatus('unauthorized');
          return;
        }

        // Check if user is authorized to view this conversation
        if (!user?.id) {
          console.warn('❌ User not authenticated');
          setErrorMessage('You must be logged in to access conversations.');
          setAuthStatus('unauthorized');
          return;
        }

        // Fetch conversation details and verify user participation
        const conversation = await dbService.getConversationById(decryptedId);
        
        if (!conversation) {
          console.warn('❌ Conversation not found:', decryptedId);
          setErrorMessage('This conversation does not exist or has been deleted.');
          setAuthStatus('unauthorized');
          return;
        }

        console.log('📋 Found conversation:', conversation);

        // Check if current user is a participant in the conversation
        const isStartupUser = conversation.startup_user_id === user.id;
        const isCdmoUser = conversation.cdmo_user_id === user.id;
        const isParticipant = isStartupUser || isCdmoUser;

        if (!isParticipant) {
          console.warn('❌ User not authorized for conversation:', {
            userId: user.id,
            conversationId: decryptedId,
            startupUserId: conversation.startup_user_id,
            cdmoUserId: conversation.cdmo_user_id
          });
          setErrorMessage('You are not authorized to view this conversation. You can only access conversations where you are a participant.');
          setAuthStatus('unauthorized');
          return;
        }

        // Verify user role matches dashboard type and conversation participation
        if (userRole === 'startup' && !isStartupUser) {
          console.warn('❌ Startup user trying to access CDMO conversation');
          setErrorMessage('This conversation is not accessible from the startup dashboard.');
          setAuthStatus('unauthorized');
          return;
        }

        if (userRole === 'cdmo' && !isCdmoUser) {
          console.warn('❌ CDMO user trying to access startup conversation');
          setErrorMessage('This conversation is not accessible from the CDMO dashboard.');
          setAuthStatus('unauthorized');
          return;
        }

        console.log('✅ Conversation access validated successfully');
        setAuthStatus('authorized');
      } catch (error) {
        console.error('❌ Error validating conversation access:', error);
        setErrorMessage('An error occurred while verifying your access to this conversation.');
        setAuthStatus('error');
      }
    };

    if (user) {
      validateConversationAccess();
    }
  }, [encryptedConversationId, user, userRole]);

  // Show dashboard with verification loader in chat area instead of full page loader
  if (authStatus === 'loading') {
    console.log('🔄 ConversationGuard: Showing loading state');
    return (
      <>
        {React.cloneElement(children as React.ReactElement, { 
          validatedConversationId: null,
          isVerifyingAccess: true
        })}
      </>
    );
  }

  // Redirect to unauthorized page with message and role
  if (authStatus === 'unauthorized' || authStatus === 'error') {
    console.log('🚫 ConversationGuard: Redirecting to unauthorized page');
    const finalErrorMessage = errorMessage || 'You are not authorized to view this conversation.';
    const redirectUrl = `/unauthorized?message=${encodeURIComponent(finalErrorMessage)}&role=${userRole}`;
    
    return <Navigate to={redirectUrl} replace />;
  }

  // Authorized - render children with conversation ID context
  console.log('✅ ConversationGuard: Access authorized, rendering dashboard with conversation ID:', conversationId);
  return (
    <>
      {React.cloneElement(children as React.ReactElement, { 
        validatedConversationId: conversationId,
        isVerifyingAccess: false
      })}
    </>
  );
};

export default ConversationGuard;
